/**
 * מבצעת ניתוח מדויק לפיצול שורה למילים תוך שמירה על spans וחיבור אותיות צמודות
 */
export const parseLineToWords = (content: string): string[] => {
  // בדיקה ראשונית - אם התוכן ריק או לא תקין
  if (!content || !content.trim()) {
    return [];
  }

  // הסרת <br> tags ונירמול רווחים
  const cleanContent = content.replace(/<br\s*\/?>/gi, ' ').replace(/\s+/g, ' ').trim();
  
  // בדיקה נוספת אחרי הניקוי
  if (!cleanContent) {
    return [];
  }

  // מפצל לטוקנים ראשוני (מילים + spans)
  const tokens: string[] = [];
  const spanRegex = /<span[^>]*>.*?<\/span>/gi;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // חילוץ spans והטקסט ביניהם
  while ((match = spanRegex.exec(cleanContent)) !== null) {
    // טקסט לפני הspan
    const beforeSpan = cleanContent.slice(lastIndex, match.index);
    if (beforeSpan.trim()) {
      const beforeWords = beforeSpan.split(/\s+/).filter(w => w.trim());
      tokens.push(...beforeWords);
    }

    // הspan עצמו - כעת נפצל אותו למילים נפרדות
    const spanWords = splitSpanToWords(match[0]);
    
    // רק הוספת spans שאינם ריקים
    const validSpanWords = spanWords.filter(word => !isEmptyOrInvalidWord(word));
    tokens.push(...validSpanWords);
    lastIndex = spanRegex.lastIndex;
  }

  // שארית הטקסט
  const remainingText = cleanContent.slice(lastIndex);
  if (remainingText.trim()) {
    const remainingWords = remainingText.split(/\s+/).filter(w => w.trim());
    tokens.push(...remainingWords);
  }

  // מיזוג אותיות בודדות וסימנים עם spans/מילים צמודים
  const finalWords: string[] = [];
  
  for (let i = 0; i < tokens.length; i++) {
    const current = tokens[i];
    const prev = tokens[i - 1];
    const next = tokens[i + 1];
    
    if (current.startsWith('<span')) {
      let result = current;
      let skipNext = false;
      // בדיקה אם יש קידומת לפני הspan - רק אם הקידומת לא הייתה חלק מspan קודם
      if (prev && isSingleLetterOrPrefix(prev) && 
          (!tokens[i - 2] || !tokens[i - 2].startsWith('<span'))) {
        result = prev + result;
        // עושים pop רק אם הקידומת באמת הייתה האיבר האחרון ב-finalWords
        if (finalWords.length > 0 && finalWords[finalWords.length - 1] === prev) {
          finalWords.pop(); // הסרת הקידומת הקודמת שכבר הוספנו
        }
      }
      // בדיקה אם יש סימן פותח לפני הspan (מרכאות, סוגריים וכו')
      else if (prev && isOpeningMark(prev)) {
        result = prev + result;
        finalWords.pop(); // הסרת הסימן הפותח שכבר הוספנו
      }
      // בדיקה אם יש סימן פיסוק או אות בודדת אחרי
      if (next && isSingleLetterOrSuffix(next)) {
        result = result + next;
        skipNext = true;
      }
      
      if (!isEmptyOrInvalidWord(result)) {
        finalWords.push(result);
      }
      if (skipNext) i++; // דילוג על הטוקן הבא רק אם באמת חיברנו אותו
    } 
    // אם זה סימן פיסוק והמילה הקודמת היא span או מילה רגילה, נחבר אותו למילה הקודמת
    else if (isPunctuationOnly(current) && finalWords.length > 0) {
      finalWords[finalWords.length - 1] += current;
    }
    // אם זה לא span ולא אות בודדת שכבר טופלה ולא סימן פיסוק ולא סימן פותח שיטופל
    else if ((!isSingleLetterOrPrefix(current) && !isOpeningMark(current)) || 
             (!tokens[i + 1]?.startsWith('<span') && !tokens[i - 1]?.startsWith('<span'))) {
      // הוספה רק אם המילה לא ריקה
      if (!isEmptyOrInvalidWord(current)) {
        finalWords.push(current);
      }
    }
  }

  // ניקוי רווחים מיותרים בתוך spans וסינון מילים ריקות
  return finalWords
    .map(word => cleanSpanContent(word))
    .filter(word => !isEmptyOrInvalidWord(word));
};

/**
 * מפצלת span למילים נפרדות, כל אחת עטופה באותו span
 */
const splitSpanToWords = (spanElement: string): string[] => {
  // חילוץ attributes וcontent מהspan
  const spanMatch = spanElement.match(/<span([^>]*)>(.*?)<\/span>/);
  if (!spanMatch) {
    return [spanElement]; // אם לא מצאנו התאמה, נחזיר כמו שהוא
  }
  
  const [, attributes, content] = spanMatch;
  
  // אם אין רווחים בתוכן, אין צורך לפצל
  if (!content.includes(' ')) {
    return [spanElement];
  }
  
  // זיהוי קידומת בתחילת התוכן (וְ, בְ, כְ וכו')
  const prefixMatch = content.match(/^([\u05D5\u05D1\u05DB\u05DC\u05DE\u05D4\u05E9\u05EA\u05E6\u05E0\u05E8\u05E4][\u05B0-\u05C7]?)/u);
  const prefix = prefixMatch ? prefixMatch[1] : '';
  
  // הסרת הקידומת מהתוכן לצורך ניתוח
  const contentWithoutPrefix = prefix ? content.slice(prefix.length) : content;
  
  // זיהוי מרכאות וסימני פיסוק בתחילת ובסוף התוכן
  const startQuoteMatch = contentWithoutPrefix.match(/^(["'«])/);
  const startQuote = startQuoteMatch ? startQuoteMatch[1] : '';
  
  // זיהוי סימני פיסוק בסוף - כולל כפילות
  const endPunctuationMatch = contentWithoutPrefix.match(/([.,;:!?]+)$/);
  const endPunctuation = endPunctuationMatch ? endPunctuationMatch[1] : '';
  
  // זיהוי מרכאות סוגרות - רק אם אין פיסוק אחריהן
  let endQuote = '';
  if (!endPunctuation) {
    const endQuoteMatch = contentWithoutPrefix.match(/(["'»])$/);
    endQuote = endQuoteMatch ? endQuoteMatch[1] : '';
  }
  
  // הסרת המרכאות והסימנים לצורך פיצול
  let cleanContent = contentWithoutPrefix;
  if (startQuote) {
    cleanContent = cleanContent.slice(startQuote.length);
  }
  if (endPunctuation) {
    cleanContent = cleanContent.slice(0, -endPunctuation.length);
  }
  if (endQuote) {
    cleanContent = cleanContent.slice(0, -endQuote.length);
  }
  
  // פיצול התוכן הנקי למילים
  const words = cleanContent.split(/\s+/).filter(w => w.trim());
  
  // אם אין מילים או התוכן ריק, נחזיר מערך ריק
  if (words.length === 0) {
    return [];
  }
  
  // אם יש רק מילה אחת, נחזיר את הspan המקורי
  if (words.length === 1) {
    return [spanElement];
  }
  
  // יצירת span נפרד לכל מילה עם שמירה על קידומת, מרכאות וסימני פיסוק
  const result = words.map((word, index) => {
    let wrappedWord = word;
    
    // הוספת קידומת למילה הראשונה
    if (index === 0 && prefix) {
      wrappedWord = prefix + wrappedWord;
    }
    
    // הוספת מרכאה פותחת למילה הראשונה
    if (index === 0 && startQuote) {
      wrappedWord = startQuote + wrappedWord;
    }
    
    // הוספת מרכאה סוגרת או סימן פיסוק למילה האחרונה
    if (index === words.length - 1) {
      if (endPunctuation) {
        wrappedWord = wrappedWord + endPunctuation;
      }
      if (endQuote) {
        wrappedWord = wrappedWord + endQuote;
      }
    }
    
    return `<span${attributes}>${wrappedWord}</span>`;
  });
  
  return result;
};


/**
 * מנקה רווחים מיותרים בתוך spans
 */
const cleanSpanContent = (word: string): string => {
  if (!word.includes('<span')) {
    return word;
  }
  
  // ניקוי רווחים מיותרים בתוך תגיות span
  return word.replace(/<span([^>]*)>\s*(.*?)\s*<\/span>/g, (match, attributes, content) => {
    const cleanContent = content.trim().replace(/\s+/g, ' ');
    // אם התוכן ריק אחרי הניקוי, נחזיר מחרוזת ריקה כדי שתיסולק בסינון
    if (!cleanContent) {
      return '';
    }
    return `<span${attributes}>${cleanContent}</span>`;
  });
};

/**
 * בודק אם המילה היא אות בודדת או קידומת (וְ, בְ, לְ, שֶׁ וכו')
 */
const isSingleLetterOrPrefix = (word: string): boolean => {
  // הסרת HTML tags לבדיקה
  const cleanWord = word.replace(/<[^>]*>/g, '');
  
  // בדיקה לקידומות נפוצות - אותיות בודדות עם ניקוד שהן חלק מהמילה הבאה
  // כולל: וְ, בְ, כְ, לְ, מְ, הַ, שֶׁ, תִּ ועוד
  return cleanWord.length <= 3 && /^[\u05D5\u05D1\u05DB\u05DC\u05DE\u05D4\u05E9\u05EA\u05E6\u05E0\u05E8\u05E4\u05D0\u05D9\u05E2\u05D8\u05D6\u05D7\u05E1\u05D2\u05D3\u05E7\u05DA\u05DA\u05DD\u05E5\u05E3][\u05B0-\u05C7]?[\u05B0-\u05C7]?$/u.test(cleanWord);
};

/**
 * בודק אם המילה היא סימן פיסוק או סיומת
 */
const isSingleLetterOrSuffix = (word: string): boolean => {
  // הסרת HTML tags לבדיקה
  const cleanWord = word.replace(/<[^>]*>/g, '');
  return cleanWord.length <= 2 && /^[.,;:!?]$|^[הם][\u05B0-\u05C3]?$/.test(cleanWord);
};

/**
 * בודק אם המחרוזת היא רק סימן פיסוק
 */
const isPunctuationOnly = (word: string): boolean => {
  // הסרת HTML tags לבדיקה
  const cleanWord = word.replace(/<[^>]*>/g, '');
  return /^[.,;:!?"\-–—]+$/.test(cleanWord);
};

/**
 * בודק אם המילה היא סימן פותח שצריך להתחבר למילה הבאה
 */
const isOpeningMark = (word: string): boolean => {
  // הסרת HTML tags לבדיקה
  const cleanWord = word.replace(/<[^>]*>/g, '').trim();
  return /^["'«(]$/.test(cleanWord);
};

/**
 * בודק אם המילה ריקה או שהיא תגית ריקה (רק רווחים או בלי תוכן)
 */
const isEmptyOrInvalidWord = (word: string): boolean => {
  if (!word || !word.trim()) {
    return true;
  }
  
  // בדיקה אם זה span ריק או עם רק רווחים
  if (word.includes('<span')) {
    const content = word.replace(/<span[^>]*>(.*?)<\/span>/g, '$1');
    return !content || !content.trim();
  }
  
  // בדיקה לתכנים לא תקינים (כמו classes של תגיות שנותרו ללא תכן)
  if (word.includes('class=') || word.includes('x-22') || word.includes('para-style-override')) {
    return true;
  }
  
  // בדיקה לתכן שהוא רק סימני פיסוק או רווחים
  const cleanWord = word.replace(/<[^>]*>/g, '').trim();
  if (!cleanWord || /^[\s\u00A0\u2000-\u200B\u2028\u2029\u202F\u205F\u3000]*$/.test(cleanWord)) {
    return true;
  }
  
  return false;
}; 